import { formatCopy } from '../../../copy/format.js';
import uiCopy from '../../../copy/en/common.json' with { type: 'json' };
import { Check, Copy, AlertCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { copyToClipboard } from "../../lib/download.js";
import { Button, IconButton, type ButtonSize, type ButtonVariant } from "./Button.js";

export interface CopyButtonProps {
  value: string;
  /** Visible label; omit for an icon-only button. */
  label?: string;
  /** Contextual accessible name and tooltip, especially for icon-only copies. */
  accessibleLabel?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
  disabled?: boolean;
  onCopied?: (ok: boolean) => void;
}

export function CopyButton({ value, label, accessibleLabel, size = "md", variant = "secondary", disabled, onCopied }: CopyButtonProps) {
  const [status, setStatus] = useState<"idle" | "pending" | "copied" | "failed">("idle");
  const generation = useRef(0);
  const pending = useRef(false);
  const copied = status === "copied";

  useEffect(() => {
    generation.current += 1;
    pending.current = false;
    setStatus("idle");
    return () => { generation.current += 1; };
  }, [value]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setStatus("idle"), 1800);
    return () => clearTimeout(t);
  }, [copied]);

  async function copy() {
    if (disabled || pending.current) return;
    pending.current = true;
    const submitted = generation.current;
    setStatus("pending");
    const ok = await copyToClipboard(value);
    if (submitted !== generation.current) return;
    pending.current = false;
    setStatus(ok ? "copied" : "failed");
    onCopied?.(ok);
  }

  const Icon = copied ? Check : status === "failed" ? AlertCircle : Copy;
  const feedback = copied ? uiCopy.CopyButton.copied : status === "failed" ? uiCopy.CopyButton.copyFailedTryAgainOrSelectAnd : status === "pending" ? uiCopy.CopyButton.copying : "";
  const iconLabel = copied
    ? accessibleLabel ? formatCopy(uiCopy.CopyButton.copiedLabel, { label: accessibleLabel }) : uiCopy.CopyButton.copied
    : status === "failed"
      ? accessibleLabel ? formatCopy(uiCopy.CopyButton.retryLabel, { label: accessibleLabel }) : uiCopy.CopyButton.retryCopy
      : accessibleLabel ?? uiCopy.CopyButton.copy;
  if (!label) {
    return (
      <span className="copy-control" data-copy-state={status}>
      <IconButton
        icon={Icon}
        label={iconLabel}
        size={size}
        variant={variant}
        disabled={disabled || status === "pending"}
        onClick={() => void copy()}
      />
      <span className={status === "failed" ? "field__error text-sm" : "visually-hidden"} role="status">{feedback}</span>
      </span>
    );
  }
  return (
    <span className="copy-control" data-copy-state={status}>
    <Button icon={Icon} aria-label={accessibleLabel} size={size} variant={variant} disabled={disabled || status === "pending"} onClick={() => void copy()}>
      {copied ? uiCopy.CopyButton.copied : status === "failed" ? uiCopy.CopyButton.retryCopyAction : label}
    </Button>
    <span className={status === "failed" ? "field__error text-sm" : "visually-hidden"} role="status">{feedback}</span>
    </span>
  );
}
